import { Params } from '@angular/router';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';

/**
 * A selectable data project.
 */
export interface DataProject {
    /**
     * Key used in the URL query param to identify the project (e.g. `mopaprod`).
     */
    key: DataProjectKey;
    /**
     * User-friendly name for display purposes (e.g. in the navbar dropdown).
     */
    displayName: string;
    /**
     * Google Cloud Platform project id.
     */
    gcpProjectId: GcpProjectId;
}

export type DataProjectKey = 'mopaprod' | 'mopadev';

/** URL query param that holds the selected data project key. */
const DATA_PROJECT_QUERY_PARAM = 'dataProject';

const DATA_PROJECT_MOPA_PROD: DataProject = {
    key: 'mopaprod',
    displayName: 'MopaProd',
    gcpProjectId: 'mopa-b0dba',
};
const DATA_PROJECT_MOPA_DEV: DataProject = {
    key: 'mopadev',
    displayName: 'MopaDev',
    gcpProjectId: 'am-mopa-dev',
};

export const DATA_PROJECTS: readonly DataProject[] = [
    DATA_PROJECT_MOPA_PROD,
    DATA_PROJECT_MOPA_DEV,
];

/** Fallback used when the query param is missing or invalid. */
const DEFAULT_DATA_PROJECT = DATA_PROJECT_MOPA_PROD;

function getDataProjectByKey(key: string | null): DataProject {
    return DATA_PROJECTS.find((p) => p.key === key) ?? DEFAULT_DATA_PROJECT;
}

// ------------------------------------------------------------------------------------
// Live state — backed by the URL query param `dataProject`. The URL is the single source
// of truth; the parsed value is cached and refreshed whenever `window.location.search`
// changes (e.g. after a router navigation). Usable from any context (Angular DI, static
// fields, SQL builders) since this is a plain module — no DI, no browser storage.
// ------------------------------------------------------------------------------------

let cachedSearch: string | undefined;
let cachedProject: DataProject = DEFAULT_DATA_PROJECT;
let cachedHasParam: boolean = false;

function ensureFresh(): void {
    const s = window.location.search;
    if (s === cachedSearch) return;
    cachedSearch = s;
    const params = new URLSearchParams(s);
    cachedHasParam = params.has(DATA_PROJECT_QUERY_PARAM);
    cachedProject = getDataProjectByKey(params.get(DATA_PROJECT_QUERY_PARAM));
}

/** The currently selected data project, resolved from the URL query param. */
export function currentDataProject(): DataProject {
    ensureFresh();
    return cachedProject;
}

/** Whether a `dataProject` query param is present in the current URL. */
export function hasDataProjectParam(): boolean {
    ensureFresh();
    return cachedHasParam;
}

/**
 * Query params that keep the data-project selection in the URL across in-app navigation
 * (e.g. navbar links). Returns `null` when no selection is present, so the param is not
 * silently introduced and navigation behaves as if there was no selection.
 */
export function getDataProjectLinkQueryParams(): Params | null {
    return hasDataProjectParam()
        ? { [DATA_PROJECT_QUERY_PARAM]: currentDataProject().key }
        : null;
}

/** Select a data project: set the URL query param and reload (URL is the source of truth). */
export function changeDataProject(key: DataProjectKey): void {
    const url = new URL(window.location.href);
    url.searchParams.set(DATA_PROJECT_QUERY_PARAM, key);
    window.location.href = url.toString();
}
